import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { createLogger } from '@/shared/logging/logger'

const logger = createLogger('ProjectsIndex')
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Plus, Upload, Github, BookOpen } from 'lucide-react'
import { FreeCutLogo } from '@/components/brand/freecut-logo'
import { DiscordIcon } from '@/components/brand/discord-icon'
import { DISCORD_INVITE_URL } from '@/config/community'
import { ProjectList } from '@/features/projects/components/project-list'
import { EditProjectForm } from '@/features/projects/components/project-form'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { useProjectStore } from '@/features/projects/stores/project-store'
import { useProjectActions } from '@/features/projects/hooks/use-project-actions'
import {
  useProjects,
  useProjectsLoading,
  useProjectsError,
} from '@/features/projects/hooks/use-project-selectors'
import { cleanupBlobUrls } from '@/features/media-library/utils/media-resolver'
import type { Project } from '@/types/project'
import type { ProjectFormData } from '@/features/projects/utils/validation'
import type { ImportResult } from '@/features/project-bundle/types/bundle'
import { ProjectBundleImportControl } from '@/features/project-bundle/components/project-bundle-import-control'
import { LegacyMigrationBanner } from '@/features/projects/components/legacy-migration-banner'
import { LegacyMigrationErrors } from '@/features/projects/components/legacy-migration-errors'
import { TrashSection } from '@/features/projects/components/trash-section'
import { WorkspaceIndicator } from '@/features/workspace-gate'
import { LanguageSwitcher } from '@/shared/ui/language-switcher'

export const Route = createFileRoute('/projects/')({
  component: ProjectsIndex,
  // Clean up any media blob URLs when returning to projects page
  beforeLoad: async () => {
    cleanupBlobUrls()
    // Always reload projects from storage to get fresh data (thumbnails may have changed)
    const { loadProjects } = useProjectStore.getState()
    await loadProjects()
  },
})

function ProjectsIndex() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isLoading = useProjectsLoading()
  const projects = useProjects()
  const error = useProjectsError()
  const { loadProjects, updateProject } = useProjectActions()

  // Only show the full-page spinner for the genuine initial load — mutations
  // (delete/duplicate/update) should never blank the populated list.
  const showInitialLoadingSpinner = isLoading && projects.length === 0

  // Load projects on mount
  useEffect(() => {
    loadProjects()
  }, [loadProjects])

  const handleEditProject = (project: Project) => {
    setEditingProject(project)
  }

  const handleBundleImported = async (result: ImportResult) => {
    await loadProjects()
    navigate({ to: '/editor/$projectId', params: { projectId: result.project.id } })
  }

  const handleEditSubmit = async (data: ProjectFormData) => {
    if (!editingProject) return

    setIsSubmitting(true)
    try {
      await updateProject(editingProject.id, data)
      setEditingProject(null)
    } catch (error) {
      logger.error('Failed to update project:', error)
      toast.error(t('projects.toasts.updateFailed'), { description: t('projects.tryAgain') })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ProjectBundleImportControl onImported={handleBundleImported}>
      {({ openFilePicker }) => (
        <>
          <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="panel-header border-b border-border" data-no-marquee>
              <div className="max-w-[1920px] mx-auto px-6 py-5 flex items-center justify-between">
                <Link to="/">
                  <FreeCutLogo
                    variant="full"
                    size="md"
                    className="hover:opacity-80 transition-opacity"
                  />
                </Link>
                <div className="flex items-center gap-3">
                  <LanguageSwitcher size="md" align="end" side="bottom" />

                  <Separator orientation="vertical" className="h-6" />

                  <Button variant="outline" size="lg" className="gap-2 px-4" asChild>
                    <Link to="/docs">
                      <BookOpen className="w-4 h-4" />
                      Docs
                    </Link>
                  </Button>
                  <Button variant="outline" size="lg" className="gap-2 px-4" asChild>
                    <a href={DISCORD_INVITE_URL} target="_blank" rel="noopener noreferrer">
                      <DiscordIcon className="w-4 h-4" />
                      Discord
                    </a>
                  </Button>
                  <Button variant="outline" size="lg" className="gap-2 px-4" asChild>
                    <a
                      href="https://github.com/walterlow/freecut"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t('projects.viewOnGitHub')}
                    >
                      <Github className="w-4 h-4" />
                      GitHub
                    </a>
                  </Button>

                  <Separator orientation="vertical" className="h-6" />

                  <WorkspaceIndicator />
                  <Button
                    variant="outline"
                    size="lg"
                    className="gap-2 px-4"
                    onClick={openFilePicker}
                  >
                    <Upload className="w-4 h-4" />
                    {t('projects.importProject')}
                  </Button>
                  <Link to="/projects/new">
                    <Button size="lg" className="gap-2 px-4">
                      <Plus className="w-4 h-4" />
                      {t('projects.newProject')}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Error state */}
            {error && (
              <div className="max-w-[1920px] mx-auto px-6 py-4">
                <div className="panel-bg border border-destructive/50 rounded-lg p-4 text-destructive">
                  <p className="font-medium">{t('projects.errorLoading')}</p>
                  <p className="text-sm mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Legacy IDB migration banner — appears only when old data is present and unmigrated */}
            <div className="max-w-[1920px] mx-auto px-6 pt-6 space-y-3">
              <LegacyMigrationBanner onMigrated={loadProjects} />
              {/* Retry banner — appears only when a previous migration left failed items behind */}
              <LegacyMigrationErrors onRetried={loadProjects} />
            </div>

            {/* Loading state */}
            {showInitialLoadingSpinner ? (
              <div className="max-w-[1920px] mx-auto px-6 py-16 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-muted-foreground">{t('projects.loadingProjects')}</p>
                </div>
              </div>
            ) : (
              /* Projects List */
              <div className="max-w-[1920px] mx-auto px-6 py-8">
                <ProjectList onEditProject={handleEditProject} onImportProject={openFilePicker} />
                <TrashSection />
              </div>
            )}
          </div>

          {/* Edit Project Dialog */}
          <Dialog open={!!editingProject} onOpenChange={(open) => !open && setEditingProject(null)}>
            <DialogContent className="max-w-[1200px] w-[95vw] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl">{t('projects.form.editTitle')}</DialogTitle>
                <DialogDescription>{t('projects.form.editSubtitle')}</DialogDescription>
              </DialogHeader>
              {editingProject && (
                <EditProjectForm
                  onSubmit={handleEditSubmit}
                  onCancel={() => setEditingProject(null)}
                  defaultValues={{
                    name: editingProject.name,
                    description: editingProject.description,
                    width: editingProject.metadata.width,
                    height: editingProject.metadata.height,
                    fps: editingProject.metadata.fps,
                  }}
                  isSubmitting={isSubmitting}
                />
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </ProjectBundleImportControl>
  )
}
